import { z } from 'zod';

const MAX_PASSWORD_LENGTH = 512;
const MIN_PASSWORD_LENGTH = 14;
const MAX_REPEATED_CHARS = 3;

export const repeatedCharacterValidationMessage = `If under 20 characters, it can't have more than ${
  MAX_REPEATED_CHARS - 1
} consecutive identical characters.`;

export const minimumPasswordValidationMessage = `Must be at least ${MIN_PASSWORD_LENGTH} characters`;

export const mustHaveLetterValidationMessage =
  'Must contain at least one letter';

/**
 * This logic was taken directly from the monolith and modified
 * to fit the expectation of the parser
 * https://gitlab.com/zapier/zapier/-/blob/e2f40f0e849f22e7eb5d42ae8676b1575fd644ff/web/backend/zapier/identity/login/utils.py#L17
 */
const nonRepeatedCharacters = z.custom<string>(password => {
  if (typeof password !== 'string') {
    return false;
  }

  if (password.length >= 20) {
    return password;
  }

  let current = undefined;
  let count = 0;

  for (const char of password) {
    if (char !== current) {
      count = 1;
      current = char;
      continue;
    }

    count += 1;

    if (count === MAX_REPEATED_CHARS) {
      return false;
    }
  }

  return password;
}, repeatedCharacterValidationMessage);

type nonRepeatedCharacters = z.infer<typeof nonRepeatedCharacters>;

const passwordSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, { message: minimumPasswordValidationMessage })
  .and(z.string().max(MAX_PASSWORD_LENGTH))
  .and(
    z.string().regex(/[a-zA-Z]/, { message: mustHaveLetterValidationMessage })
  )
  .and(nonRepeatedCharacters);

const passwordValidation = (
  password: string,
  passwordSectionIsShown: boolean
) => {
  if (!passwordSectionIsShown) {
    return true;
  }

  const { success, error } = passwordSchema.safeParse(password);

  return success ? success : error?.flatten().formErrors.join(';');
};

export default passwordValidation;
